import type { Edge, Node } from '@xyflow/react';
import ELK, { type ElkNode } from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

const TASK_NODE_WIDTH = 260;
const TASK_NODE_HEIGHT = 110;
const CONDITION_NODE_WIDTH = 140;
const CONDITION_NODE_HEIGHT = 100;

const layoutOptions: Record<string, string> = {
  'elk.algorithm': 'layered',
  'elk.direction': 'DOWN',
  'elk.layered.spacing.nodeNodeBetweenLayers': '70',
  'elk.spacing.nodeNode': '40',
  'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
};

export async function layoutGraph(nodes: Node[], edges: Edge[]): Promise<{ nodes: Node[]; edges: Edge[] }> {
  if (nodes.length === 0) return { nodes, edges };

  const elkGraph: ElkNode = {
    id: 'root',
    layoutOptions,
    children: nodes.map(n => ({
      id: n.id,
      width: n.type === 'conditionNode' ? CONDITION_NODE_WIDTH : TASK_NODE_WIDTH,
      height: n.type === 'conditionNode' ? CONDITION_NODE_HEIGHT : TASK_NODE_HEIGHT,
    })),
    edges: edges.map(e => ({
      id: e.id,
      sources: [e.source],
      targets: [e.target],
    })),
  };

  const result = await elk.layout(elkGraph);
  const positioned = nodes.map(n => {
    const placed = result.children?.find(c => c.id === n.id);
    const isCondition = n.type === 'conditionNode';
    const width = isCondition ? CONDITION_NODE_WIDTH : TASK_NODE_WIDTH;
    const height = isCondition ? CONDITION_NODE_HEIGHT : TASK_NODE_HEIGHT;
    return {
      ...n,
      position: { x: placed?.x ?? 0, y: placed?.y ?? 0 },
      // Explicit dimensions so MiniMap can render node rectangles even before
      // React Flow has measured the custom node DOM.
      width,
      height,
      style: { ...(n.style ?? {}), width, height },
    };
  });
  return { nodes: positioned, edges };
}
